import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "../../lib/supabase.js";
import { useAuth } from "../../lib/auth.js";
import { Button } from "../../lib/ui.js";
import { SectionCard, errorStyle, mutedStyle } from "./common.js";
import { TimeInput12 } from "../../lib/TimeInput12.js";

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
/** Monday first, the way a week reads on a shop door. */
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];

interface DayHours {
  open: boolean;
  opens: string;
  closes: string;
}

const DEFAULT_DAY: DayHours = { open: false, opens: "09:00", closes: "17:00" };

/** Business opening hours: what the receptionist tells callers. (Each staff member's own working hours, which decide when a booking
 *  can be made, are on the Services & staff page.) */
export function HoursSection() {
  const { activeBusinessId } = useAuth();
  const [days, setDays] = useState<DayHours[]>(() => Array.from({ length: 7 }, () => ({ ...DEFAULT_DAY })));
  const [hoursSaved, setHoursSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);


  async function load() {
    if (!activeBusinessId) return;
    const { data: hours } = await supabase.from("business_hours").select("weekday, opens, closes").eq("business_id", activeBusinessId);
    const next: DayHours[] = Array.from({ length: 7 }, () => ({ ...DEFAULT_DAY }));
    for (const h of (hours ?? []) as Array<{ weekday: number; opens: string; closes: string }>) {
      next[h.weekday] = { open: true, opens: h.opens.slice(0, 5), closes: h.closes.slice(0, 5) };
    }
    setDays(next);
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBusinessId]);

  function updateDay(weekday: number, patch: Partial<DayHours>) {
    setHoursSaved(false);
    setDays((prev) => prev.map((d, i) => (i === weekday ? { ...d, ...patch } : d)));
  }

  async function handleSaveHours(e: FormEvent) {
    e.preventDefault();
    if (!activeBusinessId) return;
    setError(null);
    for (const wd of WEEK_ORDER) {
      const d = days[wd]!;
      if (d.open && d.closes <= d.opens) {
        setError(`${WEEKDAYS[wd]}: closing time must be after opening time.`);
        return;
      }
    }
    const { error: err } = await supabase.rpc("rpc_portal_save_business_hours", {
      p_business_id: activeBusinessId,
      p_hours: days.map((d, weekday) => ({ weekday, opens: d.opens, closes: d.closes, open: d.open })).filter((d) => d.open).map(({ open: _o, ...rest }) => rest),
    });
    if (err) {
      setError(err.message);
      return;
    }
    setHoursSaved(true);
  }

  return (
    <SectionCard
      title="Business hours"
      description="When you are open. The receptionist tells callers who ask. Bookings can only be made inside each staff member's own working hours, set on the Services & staff page."
    >
      <form onSubmit={handleSaveHours}>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, maxWidth: 440 }}>
          {WEEK_ORDER.map((wd) => {
            const d = days[wd]!;
            return (
              <div key={wd} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13.5 }}>
                <label style={{ width: 120, display: "flex", alignItems: "center", gap: 6 }}>
                  <input type="checkbox" checked={d.open} onChange={(e) => updateDay(wd, { open: e.target.checked })} />
                  {WEEKDAYS[wd]}
                </label>
                {d.open ? (
                  <>
                    <TimeInput12 label={`${WEEKDAYS[wd]} opens`} value={d.opens} onChange={(v) => updateDay(wd, { opens: v })} />
                    <span style={{ color: "var(--rc-text-muted)" }}>to</span>
                    <TimeInput12 label={`${WEEKDAYS[wd]} closes`} value={d.closes} onChange={(v) => updateDay(wd, { closes: v })} />
                  </>
                ) : (
                  <span style={mutedStyle}>Closed</span>
                )}
              </div>
            );
          })}
        </div>
        <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10 }}>
          <Button type="submit" variant="primary">
            Save business hours
          </Button>
          {hoursSaved && <span style={{ fontSize: 12.5, color: "var(--rc-success)" }}>Saved.</span>}
        </div>
      </form>
      {error && <p style={errorStyle}>{error}</p>}

    </SectionCard>
  );
}
